// ============================================
// Thẻ tổng hợp — `GET /feedback/staff/summary/`
// ============================================
// Số ở đây tính trên CÙNG bộ lọc với bảng bên dưới (hook tự bỏ `page`/
// `page_size`), nên đổi bộ lọc là cả thẻ lẫn bảng cùng đổi.
//
// Ba cái bẫy của payload, xử ngay tại file này:
// 1. `by_status` là dict THƯA -> `?? 0`, và duyệt từ `FEEDBACK_STATUS_ORDER`.
// 2. `rating.distribution` LUÔN đủ 5 khoá "1".."5" -> KHÔNG `?? 0` (xem dưới).
// 3. `rating.average` là `null` khi chưa ai chấm sao -> hiện "—", không hiện 0.

import { FeedbackStatus } from '@conai/shared-types';
import type { StaffFeedbackSummary } from '@conai/shared-types';
import { StatCard, StatBadge } from '../../../components/shared';
import { CheckCircleIcon, ClockIcon, ListIcon, PackageIcon } from '../../../components/icons';
import { EMPTY_VALUE, FEEDBACK_RATING_ORDER, useFeedbackText } from '../feedbackText';

interface FeedbackSummaryCardsProps {
  summary?: StaffFeedbackSummary;
  isLoading: boolean;
  /**
   * `/feedback/staff/summary/` hỏng.
   *
   * Endpoint này ĐỘC LẬP với `/feedback/staff/` — nó chết một mình được, trong
   * khi bảng bên dưới vẫn đầy dữ liệu. Nuốt lỗi rồi `return null` thì cả khối
   * này biến mất không một lời nào, và người dùng đọc màn hình ra "cửa hàng
   * chưa có số liệu" thay vì "số liệu chưa tải được".
   */
  isError?: boolean;
  /** Gọi lại riêng query summary — KHÔNG đụng tới danh sách. */
  onRetry?: () => void;
}

function StarIcon({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <path d="M10 2.5l2.35 4.76 5.25.76-3.8 3.7.9 5.23L10 14.48l-4.7 2.47.9-5.23-3.8-3.7 5.25-.76L10 2.5z" />
    </svg>
  );
}

function SkeletonBlock({ className }: { className?: string }) {
  return <div className={`animate-pulse rounded-xl bg-slate-200/70 ${className ?? ''}`} />;
}

export function FeedbackSummaryCards({
  summary,
  isLoading,
  isError = false,
  onRetry,
}: FeedbackSummaryCardsProps) {
  const { tr } = useFeedbackText();

  if (isLoading && !summary) {
    return (
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <SkeletonBlock key={index} className="h-[76px]" />
        ))}
      </div>
    );
  }

  /**
   * Dải amber, KHÔNG phải modal và cũng không phải khối đỏ chiếm hết màn.
   *
   * Đây là phần phụ trợ: bảng góp ý bên dưới vẫn đọc và xử lý được bình thường,
   * nên chỉ cần nói đúng một câu là số tổng hợp chưa tải được, kèm lối thử lại.
   */
  const errorBanner = isError ? (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
      <span className="font-medium">
        {tr('feedbackSummaryLoadError', 'Could not load the summary figures. The list below is unaffected.')}
      </span>
      {onRetry ? (
        <button
          type="button"
          onClick={onRetry}
          className="ml-auto rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-xs font-semibold text-amber-800 transition-colors hover:bg-amber-100"
        >
          {tr('feedbackRetry', 'Try again')}
        </button>
      ) : null}
    </div>
  ) : null;

  // Lỗi mà chưa có dữ liệu nào -> chỉ còn dải thông báo. Không lỗi và cũng không
  // có dữ liệu -> vẫn `null` như cũ. Còn dữ liệu CŨ kèm lỗi thì hiện cả hai: số
  // vẫn đọc được, nhưng phải nói rõ là nó có thể đã lạc hậu.
  if (!summary) return errorBanner;

  const { rating } = summary;

  // `rating.average` là `null` khi `rating.count === 0`. `?? 0` ở đây sẽ hiện
  // "0.0" — tức "khách chấm 0 sao", trong khi sự thật là CHƯA AI chấm.
  const averageText = rating.average == null ? EMPTY_VALUE : rating.average.toFixed(1);

  // Dict THƯA: trạng thái chưa có bản ghi nào thì BE không phát khoá.
  const resolvedCount = summary.by_status[FeedbackStatus.RESOLVED] ?? 0;

  const topIssues = summary.top_product_issues.slice(0, 5);
  const maxReports = topIssues.reduce((max, item) => Math.max(max, item.reports), 0);

  return (
    <div className="space-y-3">
      {errorBanner}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          variant="compact"
          icon={<ListIcon size={20} className="text-primary-600" />}
          iconBg="bg-blue-50"
          label={tr('feedbackStatTotal', 'Total feedback')}
          value={summary.total.toLocaleString()}
        />

        <StatCard
          variant="compact"
          icon={<ClockIcon size={20} className="text-amber-500" />}
          iconBg="bg-amber-50"
          label={tr('feedbackStatOpen', 'Awaiting action')}
          value={summary.open.toLocaleString()}
          valueColor={summary.open > 0 ? 'text-amber-600' : undefined}
          badge={
            summary.open > 0 ? (
              <StatBadge variant="warning">{tr('feedbackStatOpenBadge', 'To do')}</StatBadge>
            ) : undefined
          }
        />

        <StatCard
          variant="compact"
          icon={<CheckCircleIcon size={20} className="text-emerald-500" />}
          iconBg="bg-emerald-50"
          label={tr('feedbackStatResolved', 'Resolved')}
          value={resolvedCount.toLocaleString()}
        />

        <StatCard
          variant="compact"
          icon={
            <span className="text-amber-400">
              <StarIcon size={20} />
            </span>
          }
          iconBg="bg-amber-50"
          label={tr('feedbackStatAverageRating', 'Average rating')}
          value={averageText}
          badge={
            <StatBadge variant="neutral">
              {/* "Rated: n" chứ không phải "n ratings": tiếng Ba Lan/Ukraina có
                  ba dạng số nhiều mà `t()` chưa hỗ trợ plural rules. */}
              {tr('feedbackRatedCount', 'Rated: {{count}}', { count: rating.count })}
            </StatBadge>
          }
        />
      </div>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        {/* Phân bố sao */}
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="mb-3 text-[11px] font-bold uppercase tracking-[0.55px] text-slate-500">
            {tr('feedbackRatingDistribution', 'Rating distribution')}
          </p>

          {rating.count === 0 ? (
            <p className="py-4 text-sm text-slate-500">
              {tr('feedbackNoRatingsYet', 'No star ratings in this selection yet.')}
            </p>
          ) : (
            <div className="space-y-2">
              {[...FEEDBACK_RATING_ORDER].reverse().map((star) => {
                // `distribution` LUÔN đủ 5 khoá "1".."5" (BE bảo đảm, ngược với
                // ba dict `by_*`), nên KHÔNG `?? 0` ở đây — thêm vào chỉ khiến
                // người sau tưởng khoá có thể thiếu và đi bọc phòng thủ khắp nơi.
                const value = rating.distribution[`${star}`];
                const percent = Math.round((value / rating.count) * 100);
                return (
                  <div key={star} className="flex items-center gap-3">
                    <span className="flex w-10 shrink-0 items-center gap-1 text-xs font-semibold text-slate-600">
                      {star}
                      <span className="text-amber-400">
                        <StarIcon size={12} />
                      </span>
                    </span>
                    <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-100">
                      <div
                        className="h-full rounded-full bg-amber-400 transition-[width]"
                        style={{ width: `${percent}%` }}
                      />
                    </div>
                    <span className="w-16 shrink-0 text-right text-xs text-slate-500">
                      {value.toLocaleString()} ({percent}%)
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Sản phẩm bị báo lỗi nhiều nhất */}
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="mb-3 text-[11px] font-bold uppercase tracking-[0.55px] text-slate-500">
            {tr('feedbackTopProductIssues', 'Most reported products')}
          </p>

          {topIssues.length === 0 ? (
            <p className="py-4 text-sm text-slate-500">
              {tr('feedbackNoProductIssues', 'No product issues in this selection.')}
            </p>
          ) : (
            <ul className="space-y-2">
              {topIssues.map((item) => (
                // Khoá theo `product_id`, KHÔNG theo tên: FK `on_delete=SET_NULL`
                // nên nhiều sản phẩm đã xoá cùng có `product_name === null`, key
                // theo tên thì React coi chúng là một dòng.
                <li key={item.product_id} className="flex items-center gap-3">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
                    <PackageIcon size={14} />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm text-slate-700">
                      {/* Tên có thể `null` cùng lúc với id vẫn còn — hiện chữ
                          thay vì một ô trống không ai đọc được. */}
                      {item.product_name || (
                        <span className="italic text-slate-400">
                          {tr('feedbackDeletedProduct', 'Product no longer available')}
                        </span>
                      )}
                    </span>
                    <span className="mt-1 block h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                      <span
                        className="block h-full rounded-full bg-[#EF4444]/70"
                        style={{ width: maxReports > 0 ? `${(item.reports / maxReports) * 100}%` : '0%' }}
                      />
                    </span>
                  </span>
                  <span className="shrink-0 text-sm font-bold text-slate-700">{item.reports}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
